import gql from 'graphql-tag';

export const FRAGMENT_BOOKING_GUARANTORS = gql`
  fragment fragmentQueryBookingGuarantors on Contact {
    id
    relationship
    firstName
    middleName
    lastName
    title
    dateOfBirth
    countryTerritory
    cityTown
    cityTownId
    countryTerritoryId
    taxCountryTerritory
    taxCountryTerritoryId
    postcode
    addressLine1
    addressLine2
    mobilePhoneAreaCode
    mobilePhoneNumber
    mobilePhone
    homePhoneAreaCode
    homePhoneNumber
    homePhone
    contactEmail
    customFieldValues {
      id
      customFieldId
      customFieldType
      labelName
      value
      customFieldInputOnBookingJourney
      customFieldMandatoryOnBookingJourney
    }
    documents {
      id
      documentSettingId
      name
      status
      stage
      fileUuid
      guarantorId
      category
      mandatory
      description
      rejectedReason
    }
    guarantorReferenceNumber
    guarantorType
  }
`;

const FRAGMENT_BOOKING_LINK_BOOKING = gql`
  fragment fragmentQueryBookingLink on Booking {
    id
    originId
    specialRateOffered
    totalPrice
    bookingAdditionalFees {
      additionalFeeCardConfigId
      name
      dueDate
      dueAmount
      installmentIndex
      installmentNumber
      feature
      paymentItem {
        status
        paidAmount
      }
    }
    checkOutCartInfo {
      totalAmountToBePaid
      paymentDueNow {
        amountDue
        details {
          name
          amount
          description
        }
      }
      bookingTotalFeeDetail {
        totalFee
      }
      paymentToDate
    }
    installmentPlans {
      id
      name
      dueDate
      amountDue
      amountDueBeforeDiscount
      isDueBeforeBookingConfirmed
      deductedDetails {
        additionalFee {
          id
          name
        }
        amount
      }
      paymentItem {
        id
        currency
        paidAmount
        totalAmount
        status
      }
    }
    tenancyOption {
      tenancyOptionId
      name
      startDate
      endDate
    }
    installment {
      name
    }
    bookingSpecialOffers {
      id
      priceChangeVisibility
      specialOfferId
      specialOfferName
    }
    bookingAdditionalFees {
      additionalFeeCardConfigId
      name
      dueDate
      dueAmount
      installmentIndex
      installmentNumber
      feature
      paymentItem {
        status
        paidAmount
      }
    }
  }
`;

export const FRAGMENT_BOOKING_LINK = gql`
  fragment fragmentBookingLink on BookingLink {
    originalBooking {
      ...fragmentQueryBookingLink
    }
    replacementBooking {
      ...fragmentQueryBookingLink
    }
    occupiedBooking {
      ...fragmentQueryBookingLink
    }
    countDown
    type
    status
    sourceAcceptedAt
    sourceRejectedAt
    targetAcceptedAt
    targetRejectedAt
  }
  ${FRAGMENT_BOOKING_LINK_BOOKING}
`;

export const FRAGMENT_BOOKING = gql`
  fragment fragmentQueryBooking on Booking {
    id
    isRolling
    sourceBookingId
    sourceRollingBooking {
      id
    }
    bookingSpecialOffers {
      id
      priceChangeVisibility
      specialOfferName
    }
    bookingJourneySkipped
    bookingLink {
      status
    }
    bookingAdditionalFees {
      name
      dueAmount
    }
    category
    isBedChangeableInSp
    isNeedManualReview
    nominationPlan {
      id
      partner {
        id
        name
        type
      }
      studentPayTo
    }
    roomType {
      roomTypeId
      name
    }
    academicYear {
      id
      name
    }
    serviceSelections {
      id
      description
      serviceId
      serviceOptionId
      serviceName
      serviceType
      serviceOptionName
      incrementPerNight
    }
    chargingStartDate
    chargingEndDate
    totalPriceAfterService
    pricePerNightAfterService
    specialRateOffered
    status
    heldToTime
    countDown
    priceForBillingCycle
    pricePerBillingCycleAfterService
    pricePerNightOriginal
    pricePerMonthOriginal
    totalPrice
    totalPriceOriginal
    totalPriceAfterTax
    checkInDate
    checkOutDate
    referral
    installment {
      installmentId
      installmentType
      name
    }
    student {
      id
      email
      account {
        id
      }
    }
    balanceInfo {
      walletBalance
      amendmentCreditBalance
    }
    tenancyOptionRateType
    tenancyLengthOption
    tenancyOption {
      tenancyOptionId
      name
      startDate
      endDate
      length
      intervalForTurnaround
    }
    property {
      id
      originId
      name
      status
      imageName
      postalCode
      addressLine1
      addressLine2
      contactEmailAddress
      contactAreaCode
      contactPhoneNumber
      managementCompanyName
      billingCycle
      currency
      areaUnitOfMeasurement
      ownerName
      country {
        id
        name
        slug
        countryCode
      }
      city {
        id
        name
        slug
        latitude
        longitude
      }
      country {
        id
        slug
        name
      }
      buildings {
        id
        originId
        name
        floors {
          id
          originId
          name
          index
        }
      }
      flatPreferencePlanSetting {
        smokingEnabled
        petEnabled
        dietaryEnabled
        genderEnabled
      }
      paymentSettings {
        paymentChannel
        paymentMethod
        isEnabled
      }
    }
    roomAllocation {
      bedId
      bedName
      flatName
      floorName
      roomName
      buildingName
    }
    contracts {
      id
      name
      content
    }
    installmentPlans {
      id
      dueDate
      amountDue
      amountDueBeforeDiscount
      isDueBeforeBookingConfirmed
      deductedDetails {
        additionalFee {
          id
          name
        }
        amount
      }
    }
    invoicedPaymentItems {
      id
      modelType
      category
      description
      currency
      paidAmount
      totalAmount
      status
      invoiceDatetime
      isDueBeforeBookingConfirmed
      chargeCreated
    }
    checkOutCartInfo {
      totalAmountToBePaid
      paymentDueNow {
        amountDue
        details {
          name
          amount
          description
        }
      }
      bookingTotalFeeDetail {
        rent {
          tenancyLength
          amountAfterTax
          originalAmount
          taxInfo {
            taxRate
            amount
            includedInPrice
          }
        }
        services {
          name
          amountAfterTax
          originalAmount
          taxInfo {
            taxRate
            amount
            includedInPrice
          }
        }
        additionalFees {
          name
          amountAfterTax
          originalAmount
          taxInfo {
            taxRate
            amount
            includedInPrice
          }
        }
        specialOffer {
          name
          amount
        }
        refundableFees {
          name
          amount
        }
        totalFee
      }
    }
    defaultCard {
      id
      type
      expireYear
      expireMonth
      cardBrand
      last4
      funding
      originId
    }
    backupCard {
      id
      type
      expireYear
      expireMonth
      cardBrand
      last4
      funding
      originId
    }
    paymentItems {
      id
      name
      status
    }
    documents {
      id
      name
      status
      stage
      fileUuid
      category
      guarantorId
      mandatory
      description
      rejectedReason
    }
    guarantorSettings {
      id
      academicYearId
      guarantorType
      guarantorTypeLabelName
      guarantorTypeDescription
    }
    currentGuarantorSetting {
      id
      academicYearId
      guarantorType
      guarantorTypeLabelName
      guarantorTypeDescription
    }
    curationTicket {
      id
      status
      note
    }
  }
`;

export const MUTATION_CREATE_BOOKING = gql`
  mutation SP_Create_Booking($input: studentCreateBookingInput!) {
    studentCreateBooking(input: $input) {
      id
    }
  }
`;

export const STUDENT_GET_BOOKING = gql`
  query SP_Student_Get_Booking($id: ID!) {
    studentGetBooking(id: $id) {
      ...fragmentQueryBooking
    }
  }
  ${FRAGMENT_BOOKING}
`;

export const STUDENT_GET_BOOKING_PAYMENT_ITEMS = gql`
  query SP_Student_Get_Booking_Payment_Items($id: ID!) {
    studentGetBooking(id: $id) {
      id
      paymentItems {
        id
        name
        status
      }
    }
  }
`;

export const GET_CHARGE_STATUS = gql`
  query SP_GetChargeStatus($chargeId: ID!) {
    getChargeStatus(chargeId: $chargeId) {
      status
    }
  }
`;

export const UPDATE_INSEPCTION_REPORT = gql`
  mutation SP_MutationUpdateInspectionReport($input: studentUpdateInspectionReportInput!) {
    studentUpdateInspectionReport(input: $input) {
      id
    }
  }
`;

export const GET_PROPERTY_PLATFORM_ACCOUNT = gql`
  query SP_GetPropertyPlatformAccount($propertyId: ID!) {
    getPropertyPlatformAccount(propertyId: $propertyId) {
      id
      name
      currency
      publishableKey
    }
  }
`;

export const GET_PERSONAL_DETAILS_FIELD_SETTING_FOR_STUDENT = gql`
  query SP_GetPersonalDetailsFieldSettingForStudent(
    $propertyId: ID!
    $academicYearId: ID!
    $model: ModelOptions!
  ) {
    getPersonalDetailsFieldSettings(
      propertyId: $propertyId
      academicYearId: $academicYearId
      model: $model
    ) {
      id
      propertyId
      academicYearId
      labelName
      fieldName
      fieldIndex
      fieldType
      inputOnBookingJourney
      mandatoryOnBookingJourney
      fieldFormat
      customFieldId
    }
  }
`;

export const STUDENT_GET_PAYMENT_PREVIEW = gql`
  query SP_StudentGetPaymentPreview($input: studentGetPaymentPreviewInput!) {
    studentGetPaymentPreview(input: $input) {
      preview {
        paymentAmount
        transactionFee
        totalAmount
        incentive {
          amount
          percentage
          paymentMethodType
        }
      }
      paymentItemDetails {
        name
        amount
      }
      previewHash
    }
  }
`;

export const MUTATION_STUDENT_CONFIRM_PAYMENT = gql`
  mutation SP_StudentConfirmPayment($input: studentConfirmPaymentInput!) {
    studentConfirmPayment(input: $input) {
      id
      success
      clientSecret
      nextAction
      declineCode
    }
  }
`;

export const STUDENT_GET_ON_BEHALF_OF_FOR_PAYMENT = gql`
  query SP_StudentGetOnBehalfOfForPayment($input: studentGetOnBehalfOfForPaymentInput!) {
    studentGetOnBehalfOfForPayment(input: $input)
  }
`;

export const GET_GUARANTOR_SETTINGS_BY_ID = gql`
  query SP_GetGuarantorSettingsById($guarantorSettingId: ID!, $academicYearId: ID!) {
    studentGetGuarantorSettingsById(
      guarantorSettingId: $guarantorSettingId
      academicYearId: $academicYearId
    ) {
      fieldSettings {
        id
        propertyId
        academicYearId
        labelName
        fieldName
        fieldIndex
        fieldType
        inputOnBookingJourney
        mandatoryOnBookingJourney
        fieldFormat
      }

      guarantorSetting {
        id
        livingCountries {
          id
          name
          slug
        }
        taxCountries {
          id
          name
          slug
        }
      }

      customFields {
        id
        type
        fieldFormat
        labelName
        inputOnBookingJourney
        mandatoryOnBookingJourney
      }

      documentSettings {
        id
        name
        description
        category
        stage
        mandatory
      }
    }
  }
`;

export const STUDENT_GET_BOOKING_CUSTOM_FIELDS_VALUES = gql`
  query Student_Get_Booking_Custom_Fields_Values($id: ID!) {
    studentGetBooking(id: $id) {
      id
      customFieldValues {
        id
        customFieldId
        customFieldType
        labelName
        value
        customFieldInputOnBookingJourney
        customFieldMandatoryOnBookingJourney
      }
    }
  }
`;

export const STUDENT_GET_BOOKING_GUARANTORS = gql`
  query Student_Get_Booking_Guarantors($id: ID!) {
    studentGetBooking(id: $id) {
      id
      guarantors {
        ...fragmentQueryBookingGuarantors
      }
    }
  }
  ${FRAGMENT_BOOKING_GUARANTORS}
`;

export const GET_LANGUAGES = gql`
  query QueryLanguages {
    getLanguages {
      languages {
        language
        abbreviation
        region
        default
      }
    }
  }
`;

export const GET_CHARGE_INFO = gql`
  query SP_GetChargeInfo($propertyId: ID!, $filters: getChargeInfoInput!) {
    getChargeInfo(propertyId: $propertyId, filters: $filters) {
      chargInfo {
        id
        bookingId
        status
        amount
        currency
        paymentMethodType
        nextAction
        paymentItems {
          id
          description
          totalAmount
        }
      }
    }
  }
`;

export const GET_BOOKING_CURATION_TICKET = gql`
  query SP_GetBookingCurationTicket($id: ID!) {
    studentGetBooking(id: $id) {
      id
      curationTicket {
        id
        status
      }
    }
  }
`;

export const SHOULD_SHOW_SPECIAL_OFFER_CODE = gql`
  query ShouldShowSpecialOfferCode($propertyId: ID!, $academicYearId: DecodedAcademicYearID!) {
    shouldShowSpecialOfferCode(propertyId: $propertyId, academicYearId: $academicYearId) {
      shouldShow
    }
  }
`;

export const STUDENT_GET_ATOA_PAYMENT_PREVIEW = gql`
  query SP_StudentGetAtoaPaymentPreview($input: studentGetAtoaPaymentPreviewInput!) {
    studentGetAtoaPaymentPreview(input: $input) {
      preview {
        paymentAmount
        transactionFee
        totalAmount
        incentive {
          amount
          percentage
          paymentMethodType
        }
      }
      paymentItemDetails {
        name
        amount
      }
      previewHash
    }
  }
`;

export const MUTATION_STUDENT_INIT_ATOA_PAYMENT = gql`
  mutation SP_StudentInitAtoaPayment($input: studentInitAtoaPaymentInput!) {
    studentInitAtoaPayment(input: $input) {
      id
      success
      externalPaymentId
    }
  }
`;

export const GET_BOOKING_CONTRCTS = gql`
  query SP_GetBookingContracts($propertyId: ID!, $bookingId: ID!) {
    getBookingContracts(propertyId: $propertyId, bookingId: $bookingId) {
      id
      name
      status
      source
      content
      fileUuid
      htmlFileUuid
      contractFileStatus
    }
  }
`;

export const STUDENT_GET_BOOKING_PAYMENT_ITEM_CHARGES = gql`
  query SP_StudentGetABookingPaymentItemCharges($propertyId: ID!, $bookingId: ID!) {
    getBookingPaymentItemCharges(propertyId: $propertyId, bookingId: $bookingId) {
      paymentItemCharges {
        charges {
          id
          status
          amount
          paymentMethodType
        }
        paymentItem {
          id
          name
        }
      }
    }
  }
`;

export const STUDENT_GET_REFERRAL_OPTION_LIST_BY_SLUG = gql`
  query SP_GetReferralOptionListBySlug(
    $propertyId: ID!
    $slug: FieldSlugOptions!
    $strategy: DefaultCustomPicklistResolveStrategyOptions
  ) {
    getReferralOptionListBySlug(propertyId: $propertyId, slug: $slug, strategy: $strategy) {
      id
      name
      options {
        id
        name
        slug
      }
    }
  }
`;

export const STUDENT_GET_REFERRAL_OPTIONS_FOR_LABEL_NAME = gql`
  query SP_GetReferralOptionForLabelName(
    $propertyId: ID!
    $slug: FieldSlugOptions!
    $strategy: DefaultCustomPicklistResolveStrategyOptions
  ) {
    getReferralOptionListBySlug(propertyId: $propertyId, slug: $slug, strategy: $strategy) {
      id
      name
      options {
        id
        name
        slug
      }
      disabledOptions {
        id
        name
        slug
        status
      }
    }
  }
`;

export const STUDENT_GET_PROPERTY_DOCUSIGN_ENABLED = gql`
  query SP_Student_Get_Property_Docusign_Enabled($propertyIds: [ID!]!) {
    studentGetPropertyDocusignEnabled(propertyIds: $propertyIds) {
      clientId
      enabled
      propertyId
    }
  }
`;

export const STUDENT_GET_BOOKING_DOCUSIGN_SIGNATURE = gql`
  query SP_Student_Get_Booking_Docusign_Signature($bookingId: ID!, $amendmentDetailId: ID) {
    studentGetBookingDocusignSignature(
      bookingId: $bookingId
      amendmentDetailId: $amendmentDetailId
    ) {
      enabled
      envelopeStatus
      signers {
        email
        name
        role
        status
      }
    }
  }
`;

export const STUDENT_CREATE_DOCUSIGN_EMBEDDED_SIGNING_VIEW = gql`
  mutation SP_Student_Create_Booking_Docusign_Embedded_Signing_View($bookingId: ID!) {
    studentCreateBookingDocusignEmbeddedSigningView(bookingId: $bookingId) {
      url
    }
  }
`;

export const STUDENT_GET_BOOKING_STATUS = gql`
  query SP_Student_Get_Booking_Status($id: ID!) {
    studentGetBooking(id: $id) {
      id
      status
    }
  }
`;

export const STUDENT_GET_BOOKING_DUE_BEFORE_CONFIRMATION_PAYMENT_ITEMS = gql`
  query SP_Student_Get_Booking_DueBeforeConfirmation_PaymentItems($id: ID!) {
    studentGetBooking(id: $id) {
      id
      dueBeforeConfirmationPaymentItems {
        id
        status
        totalAmount
      }
    }
  }
`;

export const STUDENT_GET_PAYMENT_INCENTIVES = gql`
  query SP_Student_Get_PaymentIncentives($input: StudentPaymentIncentivesInput!) {
    studentGetPaymentIncentives(input: $input) {
      amount
      name
      description
      paymentMethodType
      percentage
    }
  }
`;

export const STUDENT_GET_PAYMENT_INCENTIVE_OFFERS = gql`
  query SP_Student_Get_PaymentIncentiveOffers($bookingId: ID!, $propertyId: ID!) {
    studentGetPaymentIncentiveOffers(bookingId: $bookingId, propertyId: $propertyId) {
      name
      description
      paymentMethodType
      percentage
      thresholdAmount
    }
  }
`;
